// Add friend (docs/06 §2): QR + link are the primary flow, pasted UUID the
// fallback. "My code" renders a QR of `https://<domain>/add#<uuid>` — the
// UUID rides in the #fragment, which browsers never transmit, so it stays out
// of server and CDN logs. `/add#<uuid>` deep links (docs/06 §4) pre-fill the
// send form from `location.hash`; the hash is read client-side only.
// Scanning uses the native BarcodeDetector API when the platform has it — no
// scanner library (docs/03 §6 dependency rules).

import { useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import type { FriendRequest } from '@merkury/protocol';
import { useAccount } from '../../shell/accountContext.js';
import { SHARE_ORIGIN } from '../../shell/config.js';
import { getLang, translate, useT } from '../../i18n/index.js';
import { useScreens } from '../ScreensContext.js';
import { useIsDesktop } from '../ui/AppShell.js';
import { QrCode } from '../qr/QrCode.js';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Custom-scheme invite link (`merkury://add#<uuid>`) — INBOUND COMPAT ONLY.
 * No share surface emits it anymore: messengers don't linkify custom schemes
 * and recipients without the app can do nothing with one (field report). The
 * https SHARE_ORIGIN link is strictly better — verified App Link into the app,
 * web add-screen for everyone else. The Android manifest + MainActivity keep
 * accepting the scheme so links shared before this change still open.
 */
export function inviteLink(userId: string): string {
  return `merkury://add#${userId}`;
}

/** Extract a Merkury UUID from raw text: a bare UUID or any URL with `#<uuid>`. */
export function extractUuid(text: string): string | null {
  const trimmed = text.trim();
  if (UUID_RE.test(trimmed)) return trimmed.toLowerCase();
  const hashIndex = trimmed.indexOf('#');
  if (hashIndex >= 0) {
    const fragment = trimmed.slice(hashIndex + 1).trim();
    if (UUID_RE.test(fragment)) return fragment.toLowerCase();
  }
  return null;
}

// --- BarcodeDetector (native, optional) --------------------------------------

interface BarcodeDetectorLike {
  detect(source: unknown): Promise<Array<{ rawValue: string }>>;
}
interface BarcodeDetectorCtor {
  new (options?: { formats?: string[] }): BarcodeDetectorLike;
}

function nativeBarcodeDetector(): BarcodeDetectorCtor | undefined {
  return (globalThis as { BarcodeDetector?: BarcodeDetectorCtor }).BarcodeDetector;
}

type ScanState =
  | { phase: 'idle' }
  | { phase: 'scanning' }
  | { phase: 'unsupported' }
  | { phase: 'error'; message: string };

/** A denied permission deserves a pointer at the fix, not a shrug. */
function cameraErrorMessage(err: unknown): string {
  const name = err instanceof Error ? err.name : '';
  const lang = getLang();
  if (name === 'NotAllowedError' || name === 'SecurityError') {
    return translate(lang, 'addFriend.cameraDenied');
  }
  if (name === 'NotFoundError' || name === 'OverconstrainedError') {
    return translate(lang, 'addFriend.noCamera');
  }
  return translate(lang, 'addFriend.cameraUnavailable');
}

export function AddFriendScreen(): ReactNode {
  const t = useT();
  const isDesktop = useIsDesktop();
  const { state } = useAccount();
  const { api } = useScreens();
  const location = useLocation();
  const account = state.status === 'ready' ? state.account : undefined;

  const [pasteValue, setPasteValue] = useState('');
  const [sendResult, setSendResult] = useState<string | null>(null);
  const [sendError, setSendError] = useState<string | null>(null);
  const [requests, setRequests] = useState<{ incoming: FriendRequest[]; outgoing: FriendRequest[] }>(
    { incoming: [], outgoing: [] },
  );
  const [scan, setScan] = useState<ScanState>({ phase: 'idle' });
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const scanStopRef = useRef<(() => void) | null>(null);

  // The WebView's own origin is https://localhost — never usable by a
  // recipient — so links only form when a real public origin is known;
  // otherwise share the bare UUID (extractUuid accepts both).
  const shareValue = useMemo(() => {
    if (!account?.userId) return null;
    return SHARE_ORIGIN ? `${SHARE_ORIGIN}/add#${account.userId}` : account.userId;
  }, [account?.userId]);

  // Deep link: /add#<uuid> pre-fills the paste field (fragment never hits the
  // network — read straight from the router location, no server round trip).
  useEffect(() => {
    const fragment = location.hash.replace(/^#/, '');
    if (UUID_RE.test(fragment)) setPasteValue(fragment.toLowerCase());
  }, [location.hash]);

  useEffect(() => {
    let cancelled = false;
    void api
      .listFriendRequests()
      .then((res) => {
        if (!cancelled) setRequests(res);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [api]);

  useEffect(() => () => scanStopRef.current?.(), []);

  async function sendRequestTo(uuid: string): Promise<void> {
    setSendError(null);
    setSendResult(null);
    if (uuid === account?.userId) {
      setSendError(t('addFriend.ownId'));
      return;
    }
    try {
      await api.sendFriendRequest({ toUserId: uuid });
      setSendResult(t('addFriend.requestSent'));
      setPasteValue('');
      const refreshed = await api.listFriendRequests().catch(() => null);
      if (refreshed) setRequests(refreshed);
    } catch {
      // Blocking is invisible by design (docs/02 §3): failures here are
      // network/rate-limit shaped; blocked targets look like success upstream.
      setSendError(t('addFriend.couldNotSend'));
    }
  }

  function handlePasteSubmit(e: FormEvent): void {
    e.preventDefault();
    const uuid = extractUuid(pasteValue);
    if (!uuid) {
      setSendError(t('addFriend.notAnId'));
      return;
    }
    void sendRequestTo(uuid);
  }

  async function shareText(value: string): Promise<void> {
    const nav = navigator as Navigator & {
      share?: (data: { url?: string; text?: string }) => Promise<void>;
    };
    if (nav.share) {
      const data = value.includes('://') ? { url: value } : { text: value };
      await nav.share(data).catch(() => undefined);
      return;
    }
    await navigator.clipboard.writeText(value).catch(() => undefined);
    setSendResult(t('addFriend.copied'));
  }

  async function handleScan(): Promise<void> {
    const Detector = nativeBarcodeDetector();
    if (!Detector || !navigator.mediaDevices?.getUserMedia) {
      setScan({ phase: 'unsupported' });
      return;
    }
    setScan({ phase: 'scanning' });
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
      const detector = new Detector({ formats: ['qr_code'] });
      let running = true;
      scanStopRef.current = () => {
        running = false;
        for (const track of stream.getTracks()) track.stop();
        scanStopRef.current = null;
      };
      const video = videoRef.current;
      if (video) {
        video.srcObject = stream;
        await video.play().catch(() => undefined);
      }
      const tick = async (): Promise<void> => {
        while (running) {
          try {
            const codes = await detector.detect(videoRef.current);
            for (const code of codes) {
              const uuid = extractUuid(code.rawValue);
              if (uuid) {
                scanStopRef.current?.();
                setScan({ phase: 'idle' });
                setPasteValue(uuid);
                await sendRequestTo(uuid);
                return;
              }
            }
          } catch {
            // per-frame detect errors are normal while the camera warms up
          }
          await new Promise((r) => setTimeout(r, 250));
        }
      };
      void tick();
    } catch (err) {
      setScan({ phase: 'error', message: cameraErrorMessage(err) });
    }
  }

  function stopScan(): void {
    scanStopRef.current?.();
    setScan({ phase: 'idle' });
  }

  const content = (
    <>
      <header className="flex items-center gap-3">
        <Link
          to="/"
          aria-label={t('addFriend.backHome')}
          className="flex min-h-touch min-w-touch items-center justify-center rounded-xl bg-raised"
        >
          ←
        </Link>
        <h1 className="text-xl font-semibold">{t('addFriend.title')}</h1>
      </header>

      <section aria-label={t('addFriend.myCode')} className="flex flex-col items-center gap-3 rounded-xl bg-surface p-4">
        {shareValue ? (
          <>
            <QrCode value={shareValue} size={220} />
            <p className="break-all text-center font-mono text-xs text-muted">{shareValue}</p>
            <button
              type="button"
              onClick={() => void shareText(shareValue)}
              className="min-h-touch w-full rounded-xl bg-accent px-4 font-medium text-ink active:bg-accent-press"
            >
              {shareValue.includes('://') ? t('addFriend.shareLink') : t('addFriend.shareId')}
            </button>
          </>
        ) : (
          <p className="text-sm text-muted">{t('addFriend.registering')}</p>
        )}
      </section>

      <section aria-label={t('addFriend.scanCode')} className="flex flex-col gap-3">
        {scan.phase === 'scanning' ? (
          <>
            {/* Live camera preview for aiming; no audio, playback-only. */}
            <video ref={videoRef} muted playsInline className="w-full rounded-xl bg-raised" />
            <button
              type="button"
              onClick={stopScan}
              className="min-h-touch rounded-xl bg-raised px-4 text-ink"
            >
              {t('addFriend.stopScanning')}
            </button>
          </>
        ) : (
          <button
            type="button"
            onClick={() => void handleScan()}
            className="min-h-touch rounded-xl bg-raised px-4 font-medium text-ink"
          >
            {t('addFriend.scanFriend')}
          </button>
        )}
        {scan.phase === 'unsupported' && (
          <p role="status" className="text-sm text-muted">
            {t('addFriend.scanUnsupported')}
          </p>
        )}
        {scan.phase === 'error' && (
          <p role="alert" className="text-sm text-danger">
            {scan.message}
          </p>
        )}
      </section>

      <section aria-label={t('addFriend.pasteId')}>
        <form className="flex flex-col gap-3" onSubmit={handlePasteSubmit}>
          <label className="flex flex-col gap-1 text-sm text-ink">
            {t('addFriend.friendIdLabel')}
            <input
              type="text"
              value={pasteValue}
              onChange={(e) => setPasteValue(e.target.value)}
              autoComplete="off"
              autoCapitalize="off"
              spellCheck={false}
              placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
              className="min-h-touch rounded-xl border border-edge bg-surface px-3 font-mono text-sm text-ink"
            />
          </label>
          <button
            type="submit"
            className="min-h-touch rounded-xl bg-accent px-4 font-medium text-ink active:bg-accent-press"
          >
            {t('addFriend.sendRequest')}
          </button>
        </form>
        {sendResult && (
          <p role="status" className="mt-2 text-sm text-ok">
            {sendResult}
          </p>
        )}
        {sendError && (
          <p role="alert" className="mt-2 text-sm text-danger">
            {sendError}
          </p>
        )}
      </section>

      <section aria-label={t('addFriend.pendingRequests')} className="flex flex-col gap-2">
        <h2 className="text-sm font-medium text-ink">{t('addFriend.pending')}</h2>
        {requests.incoming.length === 0 && requests.outgoing.length === 0 && (
          <p className="text-sm text-muted">{t('addFriend.noPending')}</p>
        )}
        <ul className="flex flex-col gap-1">
          {requests.incoming.map((req) => (
            <li key={req.id} className="flex items-center gap-3 rounded-xl bg-surface p-3">
              <span className="flex-1 truncate text-ink">
                {req.fromDisplayName ?? req.fromUserId}
                <span className="ml-2 text-xs text-muted">{t('addFriend.incoming')}</span>
              </span>
              <button
                type="button"
                onClick={async () => {
                  await api.acceptFriendRequest(req.id).catch(() => undefined);
                  const refreshed = await api.listFriendRequests().catch(() => null);
                  if (refreshed) setRequests(refreshed);
                }}
                className="min-h-touch rounded-xl bg-accent px-3 text-sm text-ink active:bg-accent-press"
              >
                {t('common.accept')}
              </button>
              <button
                type="button"
                onClick={async () => {
                  await api.declineFriendRequest(req.id).catch(() => undefined);
                  const refreshed = await api.listFriendRequests().catch(() => null);
                  if (refreshed) setRequests(refreshed);
                }}
                className="min-h-touch rounded-xl bg-raised px-3 text-sm text-ink"
              >
                {t('common.decline')}
              </button>
            </li>
          ))}
          {requests.outgoing.map((req) => (
            <li key={req.id} className="flex items-center gap-3 rounded-xl bg-surface p-3">
              <span className="flex-1 truncate text-muted">
                {req.toDisplayName ?? req.toUserId}
                <span className="ml-2 text-xs">{t('addFriend.outgoingPending')}</span>
              </span>
            </li>
          ))}
        </ul>
      </section>
    </>
  );

  // Desktop: a centred card in the pane (web/plan.md — route stays /add for deep
  // links; no modal system). Compact: today's full-screen page, unchanged.
  if (isDesktop) {
    return (
      <main className="flex min-h-full items-start justify-center overflow-y-auto p-6">
        <div className="flex w-full max-w-[480px] flex-col gap-6 rounded-2xl border border-edge bg-surface p-6">
          {content}
        </div>
      </main>
    );
  }

  return (
    <main className="mx-auto flex min-h-full w-full max-w-app flex-col gap-6 p-6">
      {content}
    </main>
  );
}
